<template>
    <div id="top" class="care-page-shell encounter-registry-page">
        <div class="care-workbench sba-slide-in">
            <div class="encounter-page-intro">
                <UiPageHeader title="Patients' Visits" description="Search visits, review diagnosis records, and request pre-authorizations.">
                    <template #actions>
                        <button class="care-primary-action" :class="{ 'is-cancel': showStartPanel }" type="button" v-if="isReady"
                            @click.prevent="handlePrimaryStartAction">
                            <i class="material-icons" aria-hidden="true">{{ showStartPanel ? 'close' : 'add' }}</i>
                            <span>{{ showStartPanel ? 'Cancel' : 'Start Encounter' }}</span>
                        </button>
                    </template>
                </UiPageHeader>
            </div>
            <div class="care-tab-strip" v-if="isReady">
                <button v-for="tab in boardTabs" :key="tab.id" type="button" class="care-tab-button"
                    :class="{ 'is-active': currBoardTab?.id === tab.id }" @click.prevent="handleBoardTab(tab)">
                    <i class="material-icons">{{ tab.icon }}</i>
                    <span>{{ tab.title }}</span>
                </button>
            </div>

            <div class="care-filter-shell" v-if="showEncounterList">
                <form class="care-search-form" @submit.prevent="handleSearch">
                    <div class="input-field">
                        <input id="search" type="search" v-model="searchItem" placeholder="Search by Name, ID, Client..."
                            autocomplete="off" aria-label="Search visits by name, enrollee ID, or client" />
                        <label class="label-icon" for="search"><i class="material-icons">search</i></label>
                        <button type="button" class="care-search-clear" @click="handleClearSearch" v-if="searchItem" aria-label="Clear visit search"><i class="material-icons" aria-hidden="true">close</i></button>
                    </div>
                </form>
                <div class="care-history-period-options" v-if="isReady && isHistoryTab" role="tablist" aria-label="Encounter history month">
                    <button v-for="period in historyPeriods" :key="period.id" type="button"
                        class="care-history-period-button"
                        :class="{ 'is-active': currentHistoryPeriod === period.id }"
                        :aria-selected="currentHistoryPeriod === period.id"
                        @click.prevent="handleHistoryPeriod(period.id)">
                        {{ period.title }}
                    </button>
                </div>
            </div>

            <div class="care-start-search-card" v-if="showStartSearchPrompt">
                <i class="material-icons care-start-search-icon">info_outline</i>
                <div class="care-start-search-copy">
                    <span>No current visit found for</span>
                    <strong>{{ completedSearchItem }}</strong>
                </div>
                <a href="#" class="care-start-search-link" @click.prevent="handleStartMissingSearch">
                    Click to start encounter
                </a>
            </div>

            <!-- Loader -->
            <div class="progress" v-if="isLoading">
                <div class="indeterminate"></div>
            </div>

        <!-- <div class="row">
            <div class="col s12"> -->

        <!-- Vetting Marker -->
        <div id="review"></div>

        <LoadingDiv v-if="!isReady" />

        <div v-else>

            <div class="care-start-panel" v-if="showStartPanel">
                <EnrolleeEncounter ref="enrolleeEncounterDetail" @restart="handleRestartStart" @refresh="handleRefreshStart" :serverDate="serverDate"
                    :searchPeriod="searchPeriod" :enrollee="enrollee"
                    :messages-initially-expanded="messagesInitiallyExpanded" v-if="enrollee" />
                <StartEncounter @next="handleNextStart" :serverTime="serverTime" :initialSearch="pendingStartSearch" v-else />
            </div>

            <div class="section" v-else>

                <div class="care-state-note is-error" v-if="error">
                    <i class="material-icons">info</i>
                    <span>{{ error }}</span>
                </div>

                <div class="card care-encounter-card" v-for="(encounter, index) in encounters" :key="encounter.id">
                    <!-- Main -->
                    <div class="card care-encounter-main">
                        <div class="card-content care-table-head hide-on-med-and-down">
                            <div class="row">
                                <div class="col s1 center-align">#{{ numberBadge(index) }}</div>
                                <div class="col s3">Enrollee <small>(Bio)</small></div>
                                <div class="col s2">ID <small>(Scheme)</small></div>
                                <div class="col s3">Diagnosis</div>
                                <div class="col s2">Time <small>{{!encounter?.claims?.find(x => x.authorization === 'z'
                                    && x.status === 'a') ?
                                    '(Response)' : '(Request)' }}</small></div>
                                <div class="col s1"><br /></div>
                            </div>
                        </div>

                        <div class="card-content care-encounter-row hide-on-med-and-down" @mouseenter.prevent="handleHover(encounter, 'in')"
                            @mouseleave.prevent="handleHover(encounter, 'out')">
                            <div class="row">
                                <div class="col s1">
                                    <img alt="" :src="encounter.profile_img" width="64" class="circle responsive-img right"
                                        v-if="encounter.profile_img" />
                                    <img alt="" :src="`https://ui-avatars.com/api/?name=${encounter.name}&size=128`" width="64"
                                        class="circle responsive-img right" v-else />
                                </div>
                                <div class="col s3">
                                    <h6>
                                        <span class="new badge" data-badge-caption="principal"
                                            v-if="encounter.type == 'p'"></span>
                                        <span class="new badge orange" data-badge-caption="dependant"
                                            v-if="encounter.type == 'd'"></span>
                                        <b>{{ encounter.name }}</b>
                                        <br />
                                        <small><span class="blue-text"><b>{{
                                            showAgeSex(encounter?.dob, encounter?.sex)
                                                    }}</b></span></small>
                                        <br />
                                        {{ encounter.phone }}
                                        <br />
                                        <span class="care-encounter-date-chip">
                                            <i class="material-icons">event</i>
                                            {{ encounterDateLabel(encounter) }}
                                        </span>
                                        <br /><br />
                                        <span class="chip black-text text-accent-3 pointable"
                                            :title="encounter.picked_provider"
                                            v-if="encounter.picked_provider_id && encounter.provider_id && encounter.picked_provider_id !== encounter.provider_id">Referral</span>
                                    </h6>
                                </div>
                                <div class="col s2">
                                    <p>
                                        {{ encounter.enrollee_number }}
                                        <br />
                                        (<b>{{ encounter.scheme.name.toUpperCase() }}</b>)
                                        <br />
                                        <small>{{ encounter.client_name }}</small>
                                    </p>
                                </div>
                                <div class="col s3">
                                    <p class="orange-text text-accent-3" v-if="encounter.diagnosis">
                                        {{ encounter.diagnosis }}
                                    </p>
                                    <p class="chip yellow" v-else>No Diagnosis</p>
                                </div>
                                <div class="col s2 care-encounter-activity">
                                    <button type="button" class="care-request-timer-chip"
                                        :class="{ 'is-complete': !hasPendingAuthRequest(encounter), 'is-pending': hasPendingAuthRequest(encounter) }"
                                        :title="hasPendingAuthRequest(encounter) ? 'Pending request' : 'Complete'"
                                        disabled>
                                        <i class="material-icons">{{ hasPendingAuthRequest(encounter) ? "schedule" : "check_circle" }}</i>
                                        <span>{{ encounterTimeLabel(encounter) }}</span>
                                    </button>
                                    <button type="button" class="care-message-link"
                                        :class="{ 'has-unread': encounterUnreadMessageCount(encounter) }" v-if="isCurrentTab"
                                        :title="encounterMessageLabel(encounter)"
                                        :aria-label="`${encounterMessageLabel(encounter)}. Open messages.`"
                                        @click.prevent.stop="handleOpenMessages(encounter)">
                                        <i class="material-icons">forum</i>
                                        <span>{{ encounterMessageCount(encounter) }}</span>
                                        <small>Messages</small>
                                        <b class="care-message-unread" v-if="encounterUnreadMessageCount(encounter)">
                                            {{ encounterUnreadMessageCount(encounter) > 99 ? "99+" : encounterUnreadMessageCount(encounter) }}
                                        </b>
                                    </button>
                                </div>
                                <div class="col s1">
                                    <button type="button" class="right care-row-action" v-if="canEditEncounter(encounter)"
                                        @click.prevent="handleUpdate(encounter)" aria-label="Request authorization" title="Request authorization">
                                        <i class="material-icons" aria-hidden="true">insert_comment</i>
                                    </button>
                                    <div class="care-history-action-stack right" v-else>
                                        <span class="care-history-lock-action" :title="encounterLockTitle(encounter)">
                                            <i class="material-icons">{{ encounterLockIcon(encounter) }}</i>
                                        </span>
                                        <button type="button" class="care-history-move-action" v-if="isHistoryTab"
                                            :disabled="!canMoveToCurrent(encounter) || isMovingToCurrent(encounter)"
                                            :title="moveToCurrentTitle(encounter)"
                                            :aria-label="moveToCurrentTitle(encounter)"
                                            @click.prevent.stop="handleMoveToCurrent(encounter)">
                                            <i class="material-icons">{{ isMovingToCurrent(encounter) ? "hourglass_empty" : "visibility" }}</i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- Mobile -->
                    <div class="card-content care-encounter-row hide-on-large-only" @mouseenter.prevent="handleHover(encounter, 'in')"
                        @mouseleave.prevent="handleHover(encounter, 'out')">

                        <table class="responsive-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <td>Name <small>(Age/Sex)</small></td>
                                    <td>ID <small>(Scheme)</small></td>
                                    <td>Diagnosis</td>
                                    <td>Time</td>
                                    <td></td>
                                </tr>
                            </thead>

                            <tbody>
                                <tr>
                                    <td>{{ numberBadge(index) }} ({{ encounter.scheme.name.toUpperCase() }})</td>
                                    <td>
                                        <b>{{ encounter.name }}</b> &nbsp;

                                        <small><span class="teal-text"><b>{{
                                            showAgeSex(encounter.dob, encounter.sex)
                                                    }}</b></span> </small>
                                        <br /><br />
                                        <span class="chip black-text text-accent-3 pointable"
                                            :title="encounter.picked_provider"
                                            v-if="encounter.picked_provider_id && encounter.provider_id && encounter.picked_provider_id !== encounter.provider_id">Referral</span>
                                        <br />
                                        <span class="care-encounter-date-chip">
                                            <i class="material-icons">event</i>
                                            {{ encounterDateLabel(encounter) }}
                                        </span>
                                    </td>
                                    <td>
                                        {{ encounter.enrollee_number }}

                                    </td>
                                    <td>
                                        <span class="orange-text text-accent-3" v-if="encounter.diagnosis">
                                            {{ encounter.diagnosis }}
                                        </span>
                                        <span class="chip yellow" v-else>No Diagnosis</span>
                                    </td>
                                    <td class="care-encounter-activity">
                                        <button type="button" class="care-request-timer-chip"
                                            :class="{ 'is-complete': !hasPendingAuthRequest(encounter), 'is-pending': hasPendingAuthRequest(encounter) }"
                                            :title="hasPendingAuthRequest(encounter) ? 'Pending request' : 'Complete'"
                                            disabled>
                                            <i class="material-icons">{{ hasPendingAuthRequest(encounter) ? "schedule" : "check_circle" }}</i>
                                            <span>{{ encounterTimeLabel(encounter) }}</span>
                                        </button>
                                        <button type="button" class="care-message-link"
                                            :class="{ 'has-unread': encounterUnreadMessageCount(encounter) }" v-if="isCurrentTab"
                                            :title="encounterMessageLabel(encounter)"
                                            :aria-label="`${encounterMessageLabel(encounter)}. Open messages.`"
                                            @click.prevent.stop="handleOpenMessages(encounter)">
                                            <i class="material-icons">forum</i>
                                            <span>{{ encounterMessageCount(encounter) }}</span>
                                            <small>Messages</small>
                                            <b class="care-message-unread" v-if="encounterUnreadMessageCount(encounter)">
                                                {{ encounterUnreadMessageCount(encounter) > 99 ? "99+" : encounterUnreadMessageCount(encounter) }}
                                            </b>
                                        </button>
                                    </td>
                                    <td>
                                        <button type="button" class="care-row-action"
                                            @click.prevent="handleUpdate(encounter)" v-if="canEditEncounter(encounter)">Request
                                            <i class="material-icons"
                                                aria-hidden="true">insert_comment</i></button>
                                        <div class="care-history-action-stack" v-else>
                                            <span class="care-history-lock-action" :title="encounterLockTitle(encounter)">
                                                <i class="material-icons">{{ encounterLockIcon(encounter) }}</i>
                                            </span>
                                            <button type="button" class="care-history-move-action" v-if="isHistoryTab"
                                                :disabled="!canMoveToCurrent(encounter) || isMovingToCurrent(encounter)"
                                                :title="moveToCurrentTitle(encounter)"
                                                :aria-label="moveToCurrentTitle(encounter)"
                                                @click.prevent.stop="handleMoveToCurrent(encounter)">
                                                <i class="material-icons">{{ isMovingToCurrent(encounter) ? "hourglass_empty" : "visibility" }}</i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    </div>

                    <!-- Approvals -->
                    <div class="card care-claims-card" v-if="encounter.claims.length">
                        <ol class="collection" :class="{
                            'blue-grey lighten-5': (index + 1) % 2 === 1,
                        }">
                            <span v-for="claim in encounter.claims.slice().reverse()" :key="claim.id">
                                <!-- approved -->
                                <span v-if="claim.authorization == 'y'">
                                    <li class="collection-item"
                                        :class="{ 'grey lighten-2': claim.sent_price == 0, 'green lighten-4': claim.sent_price != 0 }"
                                        v-if="claim.sent_price === claim.approved_price">
                                        <b>{{ claim.item }}</b> x {{ claim.sent_qty }}
                                        (&#8358;{{
                                            Number(claim?.sent_price)
                                                .toFixed(2)
                                                .toString()
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                        }}) <span v-if="claim.sent_price != 0">-> (Approved)</span> ->
                                        <span class="blue-text tooltipped pointable" data-position="top"
                                            :data-tooltip="claim.comment" v-if="claim.comment">({{
                                                claim.authorization_code }})</span>
                                        <span class="black-text" v-else>({{ claim.authorization_code }})</span>

                                        <span class="secondary-content pointable"
                                            :class="{ 'black-text': claim.sent_price == 0, 'green-text': claim.sent_price != 0 }"
                                            :title="claim.sent_price == 0 ? 'Completed' : 'Approved'">
                                            <i class="material-icons care-item-status-icon" aria-hidden="true">check_circle</i>
                                            <span>{{ claim.sent_price == 0 ? 'Completed' : 'Approved' }}</span>
                                        </span>
                                    </li>
                                    <li class="collection-item blue lighten-4" v-else>
                                        <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                                        &#8358;{{
                                            Number(claim?.sent_price)
                                                .toFixed(2)
                                                .toString()
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                        }}) -> x {{ claim.approved_qty }} (Approved: <b> &#8358;{{
                                            Number(claim?.approved_price)
                                                .toFixed(2)
                                                .toString()
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                        }}</b>) ->
                                        <span class="blue-text text-darken-4 tooltipped pointable" data-position="top"
                                            :data-tooltip="claim.comment" v-if="claim.comment">({{
                                                claim.authorization_code }})</span>
                                        <span class="black-text" v-else>({{ claim.authorization_code }})</span>

                                        <span class="secondary-content green-text pointable" title="Approved">
                                            <i class="material-icons care-item-status-icon" aria-hidden="true">check_circle</i><span>Approved</span>
                                        </span>
                                    </li>
                                </span>

                                <!-- pending -->
                                <li class="collection-item lime lighten-4 pointable care-pending-request-row"
                                    v-if="claim.authorization == 'z' && claim.status == 'a'"
                                    :class="{ 'is-readonly': !canEditEncounter(encounter) }"
                                    :title="canEditEncounter(encounter) ? 'Delete pending request' : 'Pending request'"
                                    @click.prevent.stop="canEditEncounter(encounter) && handlePendingRequestOpen(encounter, claim)">
                                    <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                                    &#8358;{{
                                        Number(claim?.sent_price)
                                            .toFixed(2)
                                            .toString()
                                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                    }})

                                    <span class="secondary-content orange-text pointable"
                                        :title="canEditEncounter(encounter) ? 'Delete pending request' : 'Pending request'">
                                        <i class="material-icons care-item-status-icon" aria-hidden="true">watch_later</i><span>Pending</span>
                                    </span>
                                </li>

                                <!-- primary care -->
                                <li class="collection-item white care-primary-care-row"
                                    v-if="claim.authorization == 'n' && claim.status == 'a'"
                                    :class="{ 'is-deletable': canEditEncounter(encounter) && isDeletableCareClaim(claim), 'is-readonly': !canEditEncounter(encounter) || !isDeletableCareClaim(claim) }"
                                    :title="canEditEncounter(encounter) && isDeletableCareClaim(claim) ? 'Delete primary care item' : 'Primary care'"
                                    @click.prevent.stop="canEditEncounter(encounter) && isDeletableCareClaim(claim) && handlePendingRequestOpen(encounter, claim)">
                                    <b>{{ claim.item }}</b> x {{ claim.sent_qty }} (Charged:
                                    &#8358;{{ formatMoney(claim?.sent_price) }})

                                    <span class="secondary-content care-primary-care-chip"
                                        :title="canEditEncounter(encounter) && isDeletableCareClaim(claim) ? 'Delete primary care item' : 'Primary care'">
                                        <i class="material-icons" v-if="canEditEncounter(encounter) && isDeletableCareClaim(claim)">delete</i>
                                        <span>Primary care</span>
                                    </span>
                                </li>

                                <!-- declined -->
                                <li class="collection-item red lighten-4"
                                    v-if="claim.authorization == 'z' && claim.status == 'd'">
                                    <s>
                                        <span class="tooltipped pointable" data-position="top"
                                            :data-tooltip="claim.comment" v-if="claim.comment">
                                            <b>{{ claim.item }}</b> x
                                            {{ claim.sent_qty }} (Charged: &#8358;{{
                                                Number(claim?.sent_price)
                                                    .toFixed(2)
                                                    .toString()
                                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }})
                                        </span>

                                        <span v-else>
                                            <b>{{ claim.item }}</b> x
                                            {{ claim.sent_qty }} (Charged: &#8358;{{
                                                Number(claim?.sent_price)
                                                    .toFixed(2)
                                                    .toString()
                                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                            }})
                                        </span>
                                    </s>

                                    <span class="secondary-content red-text pointable" title="Declined">
                                        <i class="material-icons care-item-status-icon" aria-hidden="true">cancel</i><span>Declined</span>
                                    </span>
                                </li>
                                <div class="divider"></div>
                            </span>
                        </ol>
                    </div>

                </div>
            </div>

            <div class="section center" v-if="showEncounterList && !encounters?.length">
                <h6>
                    <img alt="" src="@/assets/img/no-user.png" width="256" class="circle responsive-img" />
                </h6>
                <p class="blue-grey-text">{{ emptyMessage }}</p>
            </div>

            <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="hasPages && encounters.length" />
        </div>

        </div>
    </div>

    <Teleport to="body">
        <div class="care-request-delete-overlay" @click.self="handlePendingRequestClose" v-if="isPendingRequestDeleteOpen">
            <section class="care-request-delete-modal" role="dialog" aria-modal="true" aria-labelledby="care-request-delete-title">
                <div class="care-request-delete-content">
                    <span>{{ deleteRequestModalCopy.label }}</span>
                    <h5 id="care-request-delete-title">{{ deleteRequestModalCopy.title }}</h5>
                    <p>
                        {{ deleteRequestModalCopy.body }}
                        <b>{{ pendingRequestClaim?.item }}</b> for
                        <b>{{ pendingRequestEncounter?.name }}</b>.
                        Sent amount: <b>&#8358;{{ formatMoney(pendingRequestClaim?.sent_price) }}</b>.
                    </p>
                </div>
                <div class="care-request-delete-footer">
                    <button type="button" class="care-request-delete-btn is-cancel"
                        :class="{ disabled: isDeletingPendingRequest }" @click.prevent="handlePendingRequestClose">
                        <i class="material-icons">close</i>
                        <span>Cancel</span>
                    </button>
                    <button type="button" class="care-request-delete-btn is-delete"
                        :class="{ disabled: isDeletingPendingRequest }" @click.prevent="handlePendingRequestDelete">
                        <i class="material-icons">{{ isDeletingPendingRequest ? "hourglass_empty" : "delete" }}</i>
                        <span>{{ isDeletingPendingRequest ? deleteRequestModalCopy.loading : deleteRequestModalCopy.button }}</span>
                    </button>
                </div>
            </section>
        </div>
    </Teleport>
    <!-- </div>
    </div> -->
</template>

<script>
import { computed, nextTick, onMounted, onUnmounted, ref, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import getItems from "@/composables/getItems";
import getItemsOfItems from "@/composables/getItemsOfItems";
import postItem from "@/composables/postItem";
import putItem from "@/composables/putItem";
import { getCachedServerDate } from "@/composables/requestOptimization";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import StartEncounter from "@/components/encounter/StartEncounterComponent.vue";
import EnrolleeEncounter from "@/components/encounter/EnrolleeEncounterComponent.vue";
import { ageSex } from "@/composables/fxLibrary/coreFx.js";
import { getCache, addCache } from "@/composables/cache/cache";
import { registerAuthorizationRealtimeTarget } from "@/composables/authorizationRealtimeCoordinator";
import { unreadEncounterMessageCount } from "@/composables/encounterMessageReadState";
import nativeUi from "@/ui/native-ui";


export default {
    name: "encounterHome",
    components: { LoadingDiv, PaginationDiv, UiPageHeader, StartEncounter, EnrolleeEncounter },
    setup() {
        const response = ref(null);
        const searchTitle = ref(null);
        const searchItem = ref(null);
        const isReady = ref(false);
        const isLoading = ref(false);
        const messagesInitiallyExpanded = ref(true);
        const isEmpty = ref(true);
        const error = ref(null);
        const curr = ref(null);
        const currUrl = ref(null);
        const currHook = ref(null);
        const home = ref(null);
        const currTab = ref(null);
        const tabs = ref([]);
        const boardTabs = ref([
            { id: "current", title: "Current", icon: "view_list" },
            { id: "history", title: "History", icon: "history" },
            { id: "start", title: "Start", icon: "add_circle_outline" },
        ]);
        const currBoardTab = ref(boardTabs.value[0]);
        const enrollee = ref(null);
        const enrolleeEncounterDetail = ref(null);
        const serverDate = ref(null);
        const serverTime = ref(null);
        const searchPeriod = ref(null);
        const isPendingRequestDeleteOpen = ref(false);
        const isDeletingPendingRequest = ref(false);
        const pendingRequestEncounter = ref(null);
        const pendingRequestClaim = ref(null);
        const completedSearchItem = ref(null);
        const pendingStartSearch = ref(null);
        const encounterRequestId = ref(0);
        const currentHistoryPeriod = ref("current");
        const historySearches = ref({ current: "", previous: "" });
        const isRestoringSearch = ref(false);
        const movingToCurrentId = ref(null);
        // const homeUrl = ref(null);

        const historyPeriods = computed(() => {
            const currentDate = new Date();
            const previousDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            const monthLabel = (date) => date.toLocaleDateString(undefined, {
                month: "long",
                year: "numeric",
            });

            return [
                { id: "current", title: monthLabel(currentDate) },
                { id: "previous", title: monthLabel(previousDate) },
            ];
        });

        // router
        const router = useRouter();
        const route = useRoute();

        // store
        const store = useStore();

        // config
        const providerId = store.state?.user?.account?.id;

        // start
        onMounted(() => {
            // page name
            document.title = "All Encounters";
        });

        // menu
        onMounted(() => {
            // load tab
            tabs.value.push({ id: 1, name: "Encounters", title: "All", icon: null, url: "encounter", hash: null, });
            tabs.value.push({ id: 2, name: "Encounters", title: "Request", icon: "add_circle", url: "encounter", hash: "#start", });

            // set default
            currTab.value = tabs.value.find(x => x.title.includes("All"));
            // set home
            home.value = tabs.value.find(x => x.title.includes("All"));

            if (route.hash === "#start") {
                handleBoardTab(boardTabs.value.find(x => x.id === "start"), { pushRoute: false });
            }
        });

        // starter
        onMounted(() => {
            // start
            loadServerTime();
            if (!route.query.messageEncounterId) {
                getStarted();
            }
        });

        // start
        const getStarted = (options = {}) => {
            completedSearchItem.value = null;
            const isHistory = currBoardTab.value?.id === "history";
            const searchLabel = String(searchItem.value || "").trim();

            if (isHistory) {
                historySearches.value[currentHistoryPeriod.value] = searchLabel;
            }

            // set current Url
            currUrl.value = isHistory
                ? "encounter/provider/history"
                : "encounter/provider";

            const params = isHistory
                ? [`history_period=${currentHistoryPeriod.value}`]
                : ["include_submitted=1"];

            if (searchLabel) {
                params.push(`q=${encodeURIComponent(searchLabel)}`);
            }

            currHook.value = `?${params.join("&")}&`;
            // get searchItem items from function
            getItemsFromApi(currUrl.value, `${providerId}${currHook.value}`, {
                searchLabel,
                skipCache: Boolean(options.skipCache),
            });
        }

        const loadServerTime = () => {
            (async () => {
                const { res } = await getCachedServerDate(getItems);

                if (res.value) {
                    serverTime.value = res.value.data;
                }
            })();
        };

        // handle curr status
        const setActiveHeaderTab = (title) => {
            const activeTab = tabs.value.find(x => x.title === title);

            if (activeTab) {
                currTab.value = activeTab;
            }
        };

        const syncRouteWithBoard = (tab) => {
            if (tab?.id === "start") {
                router.push({ name: "encounter", hash: "#start" });
                return;
            }

            if (route.name === "encounter" && route.hash) {
                router.push({ name: "encounter" });
            }
        };

        const handleActive = (tab) => {
            if (!tab) return;

            // refer
            if (tab?.url === "encounter") {
                currTab.value = tab;

                if (tab.hash === "#start") {
                    handleBoardTab(boardTabs.value.find(x => x.id === "start"), { pushRoute: true });
                    return;
                }

                handleBoardTab(boardTabs.value.find(x => x.id === "current"), { pushRoute: true });
                return;
            }

            if (tab.hash) {
                router.push({ name: tab.url, hash: tab.hash });
            } else {
                router.push({ name: tab.url });
            }
        };

        const handleBoardTab = (tab, options = {}) => {
            if (!tab) return;

            const {
                pushRoute = true,
                skipCache = false,
                initialEncounter = null,
            } = options;

            if (currBoardTab.value?.id === "history") {
                historySearches.value[currentHistoryPeriod.value] = String(searchItem.value || "").trim();
            }

            currBoardTab.value = tab;
            isRestoringSearch.value = true;
            searchItem.value = tab.id === "history"
                ? historySearches.value[currentHistoryPeriod.value]
                : null;
            nextTick(() => {
                isRestoringSearch.value = false;
            });
            response.value = initialEncounter ? [initialEncounter] : null;
            error.value = null;
            completedSearchItem.value = null;

            if (tab.id !== "start") {
                pendingStartSearch.value = null;
            }

            if (tab.id === "start") {
                setActiveHeaderTab("Request");
                if (pushRoute) {
                    syncRouteWithBoard(tab);
                }
                scrollPageTop();
                return;
            }

            setActiveHeaderTab("Home");

            isLoading.value = true;
            getStarted({ skipCache });

            if (pushRoute) {
                syncRouteWithBoard(tab);
            }
            scrollPageTop();
        };

        const handleHistoryPeriod = (period) => {
            if (!['current', 'previous'].includes(period) || period === currentHistoryPeriod.value) {
                return;
            }

            historySearches.value[currentHistoryPeriod.value] = String(searchItem.value || "").trim();
            currentHistoryPeriod.value = period;
            isRestoringSearch.value = true;
            searchItem.value = historySearches.value[period];
            nextTick(() => {
                isRestoringSearch.value = false;
            });
            response.value = null;
            error.value = null;
            isLoading.value = true;
            getStarted();
        };

        // numbering
        const numberBadge = (num) => {
            if (hasPages.value) {
                return (
                    (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
                );
            }

            return num + 1;
        };

        // ageSex
        const showAgeSex = (dob, sex) => {
            const { fx } = ageSex(dob, sex);
            return fx.value;
        };

        const hasPendingAuthRequest = (encounter) => {
            return pendingAuthRequests(encounter?.claims).length > 0;
        };

        const encounterMessageCount = (encounter) => {
            if (Number.isFinite(Number(encounter?.message_count))) {
                return Math.max(0, Number(encounter.message_count));
            }

            const messages = encounter?.stores?.encounter_messages;
            return Array.isArray(messages) ? messages.length : 0;
        };

        const encounterMessageLabel = (encounter) => {
            const count = encounterMessageCount(encounter);
            const unread = encounterUnreadMessageCount(encounter);
            const unreadLabel = unread ? `, ${unread} new` : "";
            return `${count} encounter message${count === 1 ? "" : "s"}${unreadLabel}`;
        };

        const encounterUnreadMessageCount = (encounter) => {
            return unreadEncounterMessageCount(store.state.user, encounter?.id);
        };

        const claimAuthRequestDate = (claim) => {
            const timestamp = claim?.authorization_requested_at
                || claim?.requested_at
                || claim?.sent_at
                || claim?.created_at
                || claim?.updated_at;

            const date = new Date(timestamp);
            return timestamp && !Number.isNaN(date.getTime()) ? date : null;
        };

        const pendingAuthRequests = (claims = []) => {
            return claims
                .filter(x => isPendingAuthorizationRequest(x))
                .sort((a, b) => {
                    const aDate = claimAuthRequestDate(a);
                    const bDate = claimAuthRequestDate(b);

                    if (!aDate && !bDate) return 0;
                    if (!aDate) return 1;
                    if (!bDate) return -1;

                    return aDate - bDate;
                });
        };

        const isPendingAuthorizationRequest = (claim) => {
            return claim?.authorization === "z" && claim?.status === "a";
        };

        const isPrimaryCareClaim = (claim) => {
            return claim?.authorization === "n"
                && claim?.status === "a"
                && !isSystemProfessionalFeeClaim(claim);
        };

        const isSystemProfessionalFeeClaim = (claim) => {
            return claim?.stores?.system_generated === "professional_fee";
        };

        const isDeletableCareClaim = (claim) => {
            return isPendingAuthorizationRequest(claim) || isPrimaryCareClaim(claim);
        };

        const deleteRequestModalCopy = computed(() => {
            if (isPrimaryCareClaim(pendingRequestClaim.value)) {
                return {
                    label: "Primary Care",
                    title: "Delete primary care item?",
                    body: "This will remove the primary care claim item",
                    button: "Delete Item",
                    loading: "Deleting...",
                };
            }

            return {
                label: "Pending Request",
                title: "Delete authorization request?",
                body: "This will remove the authorization request",
                button: "Delete Request",
                loading: "Deleting...",
            };
        });

        const encounterDateLabel = (encounter) => {
            const date = new Date(encounter?.encounter_date || encounter?.created_at);
            if (Number.isNaN(date.getTime())) {
                return "Encounter date unavailable";
            }

            return date.toLocaleString(
                undefined,
                {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                }
            );
        };

        const encounterTimeLabel = (encounter) => {
            const pendingAuth = pendingAuthRequests(encounter?.claims);
            if (pendingAuth.length) {
                return timeSince(claimAuthRequestDate(pendingAuth[0]));
            }

            return "Complete";
        };

        const formatMoney = (val) => {
            const amount = Number(val || 0);

            return amount
                .toFixed(2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };

        // get time
        const timeSince = (val) => {
            const past = new Date(val);
            if (!val || Number.isNaN(past.getTime())) {
                return "Pending";
            }

            const now = new Date();
            const diffMs = now - past;

            const diffMins = Math.floor(diffMs / 60000);
            if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;

            const diffHrs = Math.floor(diffMins / 60);
            if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''} ago`;

            const diffDays = Math.floor(diffHrs / 24);
            return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        }

        const canEditEncounter = (encounter) => {
            return currBoardTab.value?.id === "current"
                && encounter?.status !== "d"
                && encounter?.cluster?.status === "q";
        };

        const encounterLockIcon = (encounter) => {
            return encounter?.status === "q" ? "lock_open" : "lock";
        };

        const encounterLockTitle = (encounter) => {
            return encounter?.status === "q" ? "Encounter open" : "Encounter closed";
        };

        const canMoveToCurrent = (encounter) => {
            return isHistoryTab.value
                && Boolean(encounter?.id)
                && encounter?.status !== "d"
                && encounter?.cluster?.status === "q";
        };

        const isMovingToCurrent = (encounter) => {
            return Number(movingToCurrentId.value) === Number(encounter?.id);
        };

        const moveToCurrentTitle = (encounter) => {
            if (isMovingToCurrent(encounter)) {
                return "Moving to Current";
            }

            return canMoveToCurrent(encounter)
                ? "Move to Current"
                : "Claims batch closed";
        };

        const handleMoveToCurrent = async (encounter) => {
            if (!canMoveToCurrent(encounter) || movingToCurrentId.value !== null) {
                return;
            }

            movingToCurrentId.value = encounter.id;
            error.value = null;

            const { res, err } = await putItem("encounter/rank", encounter.id, {
                provider_id: providerId,
            });

            if (res.value?.data) {
                nativeUi.toast({
                    html: "Encounter moved to Current",
                    displayLength: 3000,
                    inDuration: 300,
                    outDuration: 300,
                });
                movingToCurrentId.value = null;
                handleBoardTab(boardTabs.value.find(x => x.id === "current"), {
                    initialEncounter: res.value.data,
                    skipCache: true,
                });
                return;
            }

            error.value = err.value || "Unable to move this encounter to Current.";
            movingToCurrentId.value = null;
        };

        const replaceEncounter = (updatedEncounter, options = {}) => {
            if (!updatedEncounter?.id) return;

            let wasReplaced = false;
            const updatedEncounters = encounters.value.map((encounter) => {
                if (encounter.id !== updatedEncounter.id) {
                    return encounter;
                }

                wasReplaced = true;
                return updatedEncounter;
            });

            if (!wasReplaced && options.addIfMissing) {
                updatedEncounters.unshift(updatedEncounter);
            }

            response.value = Array.isArray(response.value)
                ? updatedEncounters
                : {
                    ...response.value,
                    data: updatedEncounters,
                };

            addCache(`${currUrl.value}/${providerId}${currHook.value || ""}`, response.value);
        };

        const handlePendingRequestOpen = (encounter, claim = null) => {
            if (!canEditEncounter(encounter)) {
                return;
            }

            const deletableClaims = isDeletableCareClaim(claim)
                ? [claim]
                : pendingAuthRequests(encounter?.claims);
            if (!deletableClaims.length) {
                return;
            }

            pendingRequestEncounter.value = encounter;
            pendingRequestClaim.value = deletableClaims[0];
            isPendingRequestDeleteOpen.value = true;
        };

        const handlePendingRequestClose = () => {
            if (isDeletingPendingRequest.value) {
                return;
            }

            isPendingRequestDeleteOpen.value = false;
            pendingRequestEncounter.value = null;
            pendingRequestClaim.value = null;
        };

        const handlePendingRequestDelete = async () => {
            if (!pendingRequestClaim.value?.id || isDeletingPendingRequest.value) {
                return;
            }

            isDeletingPendingRequest.value = true;
            error.value = null;
            const isPrimaryCareDelete = isPrimaryCareClaim(pendingRequestClaim.value);

            const { res, err } = await postItem(`claim/auth/request/${pendingRequestClaim.value.id}/delete`, {
                status: "d",
                comment: pendingRequestClaim.value.comment || (isPrimaryCareDelete
                    ? "Primary care item deleted by provider"
                    : "Deleted by provider"),
                refresh_activity_rank: true,
            });

            if (res.value) {
                const refreshedEncounter = res.value.data;

                if (refreshedEncounter) {
                    replaceEncounter(refreshedEncounter);
                }
                nativeUi.toast({
                    html: res.value.status
                        ? (isPrimaryCareDelete ? "Primary care item deleted" : "Deleted")
                        : "Updated",
                    displayLength: 3000,
                    inDuration: 300,
                    outDuration: 300,
                });
                isPendingRequestDeleteOpen.value = false;
                pendingRequestEncounter.value = null;
                pendingRequestClaim.value = null;
            } else {
                error.value = err.value || "Unable to delete this request.";
            }

            isDeletingPendingRequest.value = false;
        };

        // get function
        const getItemsFromApi = (table, id, options = {}) => {
            const requestId = ++encounterRequestId.value;
            const searchLabel = String(options.searchLabel || "").trim();
            completedSearchItem.value = null;
            error.value = null;

            // get item from store (cache)
            let cache = options.skipCache ? null : getCache(`${table}/${id}`);

            if (cache?.length) {
                response.value = cache;
            }

            (async () => {
                const { res, err } = await getItemsOfItems(table, id);

                if (requestId !== encounterRequestId.value) {
                    return;
                }

                if (res.value) {
                    response.value = res.value.data;
                    // add cache
                    addCache(`${table}/${id}`, response.value);
                    completedSearchItem.value = currBoardTab.value?.id === "current" ? searchLabel : null;
                    // set isEmpty to false
                    // isEmpty.value = isEmpty.value && response.value.data.length ? false : true;
                } else {
                    completedSearchItem.value = null;
                    error.value = currBoardTab.value?.id === "history"
                        ? "History is ready in the UI. The provider history endpoint will be connected in the backend pass."
                        : err.value;
                }

                // page is ready, loading is complete
                isReady.value = true;
                isLoading.value = false;
            })();
        };

        // handle search (emitted from component)
        const handleSearch = () => {
            // start loading
            if (!showEncounterList.value) return;
            isLoading.value = true;
            getStarted();
        };

        // handle curr status
        const handleClearSearch = () => {
            // clear
            searchItem.value = null;
            completedSearchItem.value = null;
            pendingStartSearch.value = null;
            // restarted
            getStarted();
        };

        const handleStartMissingSearch = () => {
            const startSearch = String(completedSearchItem.value || "").trim();

            if (!startSearch) {
                return;
            }

            pendingStartSearch.value = startSearch;
            handleBoardTab(boardTabs.value.find(x => x.id === "start"));
        };

        const hasStartSearchPattern = (value) => {
            return (String(value || "").match(/\//g) || []).length >= 2;
        };

        // search watcher
        watch(searchItem, (newVal, oldVal) => {
            if (isRestoringSearch.value) {
                return;
            }

            if (!hasStartSearchPattern(newVal)) {
                completedSearchItem.value = null;
                if (currBoardTab.value?.id !== "start") {
                    pendingStartSearch.value = null;
                }
            }

            if (newVal !== oldVal) {
                handleSearch();
            }
        });

        // switch page
        const handlePage = (page) => {
            if (!hasPages.value) return;
            // start ready
            isReady.value = false;
            // go back up
            scrollPageTop();
            // get new page items from function
            getItemsFromApi(currUrl.value, `${providerId}${currHook.value}page=${page}`);
        };

        const openEncounterDetail = async (encounterId, options = {}) => {
            const {
                keepRoute = false,
                scrollToMessages = false,
                expandMessages = false,
            } = options;

            if (!encounterId) return false;

            isLoading.value = true;
            error.value = null;
            messagesInitiallyExpanded.value = expandMessages;

            const { res, err } = await getItems(`encounter/single/select/${providerId}/${encounterId}`);

            if (res.value) {
                enrollee.value = res.value.data;
                serverDate.value = null;
                searchPeriod.value = null;
                pendingStartSearch.value = null;
                completedSearchItem.value = null;
                currBoardTab.value = boardTabs.value.find(x => x.id === "start");
                setActiveHeaderTab("Request");

                if (!keepRoute) {
                    syncRouteWithBoard(currBoardTab.value);
                    scrollPageTop();
                }

                if (scrollToMessages) {
                    await nextTick();
                    window.requestAnimationFrame(() => {
                        document.querySelector(".care-start-panel .encounter-message-thread")?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    });
                }
            } else {
                error.value = err.value || "Unable to open this encounter for editing.";
            }

            isReady.value = true;
            isLoading.value = false;
            return Boolean(res.value);
        };

        // handle Edit
        const handleUpdate = async (encounter) => {
            if (!encounter?.id || !canEditEncounter(encounter)) return;

            await openEncounterDetail(encounter.id, { expandMessages: false });
        };

        const handleOpenMessages = async (encounter) => {
            if (!encounter?.id || !isCurrentTab.value) return;

            const encounterId = String(encounter.id);

            if (String(route.query.messageEncounterId || "") === encounterId) {
                await openEncounterDetail(encounterId, {
                    keepRoute: true,
                    scrollToMessages: true,
                    expandMessages: true,
                });
                return;
            }

            await router.replace({
                name: "encounter",
                query: { messageEncounterId: encounterId },
            });
        };

        const handleNextStart = async (val) => {
            messagesInitiallyExpanded.value = false;
            pendingStartSearch.value = null;
            completedSearchItem.value = null;
            enrollee.value = val.value;
            searchPeriod.value = val.searchPeriod;
            serverDate.value = val.serverDate;

            await nextTick();

            document.querySelector(".care-start-panel .encounter-date-card")?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        };

        const handleRefreshStart = (val) => {
            replaceEncounter(val);
            enrollee.value = val;
        };

        const handleRestartStart = (updatedEncounter = null) => {
            const hasFreshEncounter = Boolean(updatedEncounter?.id);

            if (hasFreshEncounter) {
                replaceEncounter(updatedEncounter, { addIfMissing: true });
            }

            messagesInitiallyExpanded.value = true;
            enrollee.value = null;
            serverDate.value = null;
            searchPeriod.value = null;
            pendingStartSearch.value = null;
            completedSearchItem.value = null;
            currBoardTab.value = boardTabs.value[0];
            setActiveHeaderTab("Home");
            isLoading.value = true;
            getStarted({ skipCache: hasFreshEncounter });
            syncRouteWithBoard(currBoardTab.value);
            scrollPageTop();
        };

        const handlePrimaryStartAction = () => {
            if (showStartPanel.value) {
                enrollee.value = null;
                serverDate.value = null;
                searchPeriod.value = null;
                handleBoardTab(boardTabs.value.find(x => x.id === "current"));
                return;
            }

            pendingStartSearch.value = null;
            completedSearchItem.value = null;
            messagesInitiallyExpanded.value = true;
            handleBoardTab(boardTabs.value.find(x => x.id === "start"));
        };

        watch(
            () => route.hash,
            (hash) => {
                if (hash === "#start" && currBoardTab.value?.id !== "start") {
                    handleBoardTab(boardTabs.value.find(x => x.id === "start"), { pushRoute: false });
                }

                if (!hash && currBoardTab.value?.id === "start") {
                    handleBoardTab(boardTabs.value.find(x => x.id === "current"), { pushRoute: false });
                }
            }
        );

        watch(
            () => route.query.messageEncounterId,
            (encounterId) => {
                if (encounterId) {
                    openEncounterDetail(encounterId, {
                        keepRoute: true,
                        scrollToMessages: true,
                        expandMessages: true,
                    });
                }
            },
            { immediate: true }
        );

        const scrollPageTop = () => {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        };

        // output results
        const encounters = computed(() => {
            let items = [];

            if (!response.value) {
                items = [];
            } else if (Array.isArray(response.value)) {
                items = response.value;
            } else if (Array.isArray(response.value.data)) {
                items = response.value.data;
            }

            return items;
        });

        // output pages
        const allPages = computed(() => {
            return response.value || {};
        });

        const hasPages = computed(() => {
            return Boolean(response.value && !Array.isArray(response.value) && response.value.current_page);
        });

        const showEncounterList = computed(() => {
            return currBoardTab.value?.id !== "start";
        });

        const showStartPanel = computed(() => {
            return currBoardTab.value?.id === "start";
        });

        const isCurrentTab = computed(() => {
            return currBoardTab.value?.id === "current";
        });

        const isHistoryTab = computed(() => {
            return currBoardTab.value?.id === "history";
        });

        const showStartSearchPrompt = computed(() => {
            return isReady.value
                && currBoardTab.value?.id === "current"
                && !isLoading.value
                && Boolean(completedSearchItem.value)
                && hasStartSearchPattern(searchItem.value)
                && hasStartSearchPattern(completedSearchItem.value)
                && !encounters.value.length
                && !error.value;
        });

        const emptyMessage = computed(() => {
            if (currBoardTab.value?.id === "history") {
                const period = historyPeriods.value.find(x => x.id === currentHistoryPeriod.value);
                return `No encounters found for ${period?.title || "this month"}.`;
            }

            return "No active encounter activity found for this provider.";
        });

        const encounterRealtimeTarget = {
            context: () => ({
                encounterId: enrollee.value?.encounter_date_set ? enrollee.value.id : null,
            }),
            refreshMessages: () => {
                return enrolleeEncounterDetail.value?.refreshMessagesFromServer?.() || false;
            },
            refreshIfSafe: () => {
                if (
                    enrollee.value
                    || isPendingRequestDeleteOpen.value
                    || isDeletingPendingRequest.value
                    || currBoardTab.value?.id === "start"
                ) {
                    return false;
                }

                isLoading.value = true;
                getStarted();
                return true;
            },
        };
        const unregisterRealtimeTarget = registerAuthorizationRealtimeTarget("encounter", encounterRealtimeTarget);
        onUnmounted(unregisterRealtimeTarget);

        // toolTip
        const toolTip = () => {
            var elems = document.querySelectorAll(".tooltipped");
            nativeUi.Tooltip.init(elems, {});
        };

        // hover effects
        const handleHover = (val, type) => {
            if (val && type) {
                if (type === "in") {
                    curr.value = val;
                }
                if (type === "out") {
                    curr.value = null;
                }
            }
        }

        // toolTip
        watchEffect(() => {
            if (response.value) {
                setTimeout(() => {
                    toolTip();
                }, 2000);
            }
        });

        return {
            handleActive,
            home,
            curr,
            currTab,
            tabs,
            boardTabs,
            currBoardTab,
            ageSex,
            showAgeSex,
            numberBadge,
            timeSince,
            hasPendingAuthRequest,
            encounterMessageCount,
            encounterUnreadMessageCount,
            encounterMessageLabel,
            encounterDateLabel,
            encounterTimeLabel,
            formatMoney,
            hasPages,
            showEncounterList,
            showStartPanel,
            isCurrentTab,
            isHistoryTab,
            historyPeriods,
            currentHistoryPeriod,
            handleHistoryPeriod,
            showStartSearchPrompt,
            emptyMessage,
            handlePage,
            handleSearch,
            handleClearSearch,
            handleStartMissingSearch,
            handleUpdate,
            handleOpenMessages,
            handleHover,
            handlePendingRequestOpen,
            handlePendingRequestClose,
            handlePendingRequestDelete,
            deleteRequestModalCopy,
            isDeletableCareClaim,
            handleBoardTab,
            handlePrimaryStartAction,
            handleNextStart,
            handleRefreshStart,
            handleRestartStart,
            isReady,
            isLoading,
            messagesInitiallyExpanded,
            isEmpty,
            error,
            isPendingRequestDeleteOpen,
            isDeletingPendingRequest,
            pendingRequestEncounter,
            pendingRequestClaim,
            canEditEncounter,
            encounterLockIcon,
            encounterLockTitle,
            canMoveToCurrent,
            isMovingToCurrent,
            moveToCurrentTitle,
            handleMoveToCurrent,
            searchItem,
            completedSearchItem,
            pendingStartSearch,
            searchTitle,
            enrollee,
            enrolleeEncounterDetail,
            serverDate,
            serverTime,
            searchPeriod,
            encounters,
            allPages,
        };
    },
};
</script>

<style scoped>
@layer components {
    .encounter-registry-page .care-workbench .care-claims-card ol.collection .collection-item { box-shadow: none !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card ol.collection > .collection-item) { border: 1px solid #dce2e8 !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item.lime) { background: #fffbeb !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item.green) { background: #ecfdf5 !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item.blue) { background: #eff6ff !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item.red) { background: #fff1f2 !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item.grey) { background: var(--ui-muted) !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card ol.collection) { background: var(--ui-card) !important; border: 0 !important; box-sizing: border-box !important; margin: 0 !important; padding: 0 !important; width: 100% !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item) { align-items: center !important; background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; display: flex !important; font-size: .8125rem !important; gap: .75rem !important; line-height: 1.6 !important; min-height: 3.5rem !important; padding: 1rem 1.25rem !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item::before) { color: var(--ui-muted-foreground) !important; flex: 0 0 1.25rem !important; font-size: .75rem !important; text-align: left !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item > .secondary-content) { align-items: center !important; background: var(--ui-muted) !important; border: 1px solid var(--ui-border) !important; border-radius: 999px !important; display: inline-flex !important; flex: 0 0 auto !important; float: none !important; font-size: .7rem !important; font-weight: 600 !important; gap: .35rem !important; line-height: 1.4 !important; margin: 0 !important; padding: .3rem .55rem !important; position: static !important; transform: none !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .secondary-content i) { font-size: 1rem !important; height: auto !important; line-height: 1 !important; margin: 0 !important; width: auto !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .secondary-content.orange-text) { background: #fef3c7 !important; border-color: #f5c451 !important; color: #854d0e !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .secondary-content.green-text) { background: #ecfdf5 !important; border-color: #a7f3d0 !important; color: #047857 !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .secondary-content.red-text) { background: #fff1f2 !important; border-color: #fecdd3 !important; color: #be123c !important; }
    @media (max-width: 600px) {
        .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item) { flex-wrap: wrap !important; }
        .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .encounter-item-copy) { flex: 1 1 calc(100% - 2rem) !important; }
        .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card .collection-item > .secondary-content) { margin-left: 2rem !important; }
    }
    .encounter-registry-page .care-claims-card ol.collection { counter-reset: encounter-item; }
    .encounter-registry-page .care-claims-card ol.collection .collection-item { counter-increment: encounter-item; padding-left: 3rem !important; }
    .encounter-registry-page .care-claims-card ol.collection .collection-item::before { color: var(--ui-muted-foreground) !important; content: counter(encounter-item) "." !important; font-size: .75rem !important; font-weight: 500 !important; left: 1rem !important; position: absolute !important; top: 1rem !important; }
    .encounter-registry-page .care-claims-card .collection { background: var(--ui-card) !important; padding: 0 !important; }
    .encounter-registry-page .care-claims-card .collection-item { background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; box-sizing: border-box; min-height: 3.5rem !important; padding: 1rem 11rem 1rem 1.25rem !important; position: relative !important; }
    .encounter-registry-page .care-claims-card .collection-item > b { color: var(--ui-foreground); font-weight: 600; }
    .encounter-registry-page .care-claims-card .collection-item > .secondary-content { align-items: center !important; background: var(--ui-muted) !important; border: 1px solid var(--ui-border) !important; border-radius: 999px !important; display: inline-flex !important; float: none !important; font-size: .7rem !important; font-weight: 600 !important; gap: .35rem !important; justify-content: center; line-height: 1.4 !important; margin: 0 !important; padding: .3rem .55rem !important; position: absolute !important; right: 1rem !important; top: 50% !important; transform: translateY(-50%) !important; }
    .encounter-registry-page .care-claims-card .collection-item > .secondary-content i { font-size: 1rem !important; height: auto !important; line-height: 1 !important; margin: 0 !important; width: auto !important; }
    .encounter-registry-page .care-claims-card .care-pending-request-row > .secondary-content { background: #fef3c7 !important; border-color: #f5c451 !important; color: #854d0e !important; }
    .encounter-registry-page .care-claims-card .collection-item > .secondary-content.green-text { background: #ecfdf5 !important; border-color: #a7f3d0 !important; color: #047857 !important; }
    .encounter-registry-page .care-claims-card .collection-item > .secondary-content.red-text { background: #fff1f2 !important; border-color: #fecdd3 !important; color: #be123c !important; }
    .encounter-registry-page .care-claims-card .divider { display: none !important; }
    @media (max-width: 600px) {
        .encounter-registry-page .care-claims-card .collection-item { padding: 1rem !important; }
        .encounter-registry-page .care-claims-card .collection-item > .secondary-content { display: flex !important; margin-top: .75rem !important; position: static !important; transform: none !important; width: fit-content !important; }
    }
    @media (min-width: 993px) {
        .encounter-registry-page .care-workbench .care-encounter-row.hide-on-large-only { display: none !important; }
    }
    @media (max-width: 992px) {
        .encounter-registry-page .care-workbench .care-table-head.hide-on-med-and-down,
        .encounter-registry-page .care-workbench .care-encounter-row.hide-on-med-and-down { display: none !important; }
    }
    .encounter-registry-page .care-start-panel :deep(.encounter-detail-card) { border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; box-shadow: none !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-create-workstation) { gap: 1rem !important; padding: 0 !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-care-card) { border: 0 !important; padding: 0 !important; }
    .encounter-registry-page .care-start-panel :deep(.claim-collection-add-card),
    .encounter-registry-page .care-start-panel :deep(.claim-add-card),
    .encounter-registry-page .care-start-panel :deep(.encounter-items-card),
    .encounter-registry-page .care-start-panel :deep(.encounter-approvals-card) { background: var(--ui-card) !important; border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; box-shadow: none !important; }
    .encounter-registry-page .care-start-panel :deep(.card-title) { color: var(--ui-foreground) !important; font-size: 1.125rem !important; font-weight: 600 !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-visit-button),
    .encounter-registry-page .care-start-panel :deep(.encounter-action-button),
    .encounter-registry-page .care-start-panel :deep(.claim-entry-button) { border-radius: calc(var(--ui-radius) - 2px) !important; box-shadow: none !important; font-size: .8125rem !important; font-weight: 600 !important; min-height: 2.5rem !important; width: auto !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-visit-save),
    .encounter-registry-page .care-start-panel :deep(.encounter-visit-auth),
    .encounter-registry-page .care-start-panel :deep(.encounter-action-submit),
    .encounter-registry-page .care-start-panel :deep(.claim-entry-primary) { background: var(--ui-primary) !important; border-color: var(--ui-primary) !important; color: var(--ui-primary-foreground) !important; }
    .encounter-registry-page .care-start-panel :deep(.encounter-visit-cancel),
    .encounter-registry-page .care-start-panel :deep(.claim-entry-cancel) { background: var(--ui-background) !important; border-color: var(--ui-border) !important; color: var(--ui-muted-foreground) !important; }
    .encounter-registry-page .care-filter-shell { align-items: center !important; display: flex !important; flex-direction: row !important; flex-wrap: wrap !important; gap: 1rem !important; justify-content: space-between !important; }
    .encounter-registry-page .care-filter-shell .care-search-form { flex: 1 1 20rem !important; min-width: 0 !important; }
    .encounter-registry-page .care-filter-shell .care-history-period-options { flex: 0 0 auto !important; max-width: 100%; }
    .encounter-registry-page .care-workbench .care-start-panel { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; padding: 1.25rem 0 !important; }
    .encounter-registry-page.care-page-shell { box-sizing: border-box !important; margin: 0 auto !important; max-width: var(--ui-content-width) !important; padding: 2rem 2rem 3rem !important; width: 100% !important; }
    .encounter-registry-page .encounter-page-intro { border-bottom: 1px solid var(--ui-border) !important; margin-bottom: 1.75rem !important; padding-bottom: 1.75rem !important; }
    .encounter-page-intro :deep(.ui-page-header) { margin: 0 !important; }
    .encounter-page-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem) !important; font-weight: 700 !important; }
    .encounter-page-intro :deep(.ui-page-header__description) { font-size: 1rem !important; line-height: 1.55 !important; }
    .encounter-registry-page .care-primary-action { background: var(--ui-primary) !important; border: 1px solid var(--ui-primary) !important; border-radius: calc(var(--ui-radius) - 2px) !important; box-shadow: none !important; color: var(--ui-primary-foreground) !important; font-size: .8125rem !important; font-weight: 600 !important; gap: .5rem !important; height: auto !important; min-height: 2.5rem !important; min-width: 0 !important; padding: .6rem 1rem !important; }
    .encounter-registry-page .care-primary-action.is-cancel { background: var(--ui-background) !important; border-color: var(--ui-border) !important; color: var(--ui-foreground) !important; }
    .encounter-registry-page .care-primary-action i { background: transparent !important; border: 0 !important; font-size: 1rem !important; height: auto !important; width: auto !important; }
    .encounter-registry-page .care-tab-strip { background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; gap: 1rem !important; margin: 0 !important; padding: 0 !important; }
    .encounter-registry-page .care-tab-button { background: transparent !important; border: 0 !important; border-bottom: 2px solid transparent !important; border-radius: 0 !important; color: var(--ui-muted-foreground) !important; font-size: .875rem !important; font-weight: 500 !important; gap: .5rem !important; height: auto !important; min-height: 2.75rem !important; padding: .65rem .75rem !important; }
    .encounter-registry-page .care-tab-button.is-active { border-bottom-color: var(--ui-primary) !important; color: var(--ui-foreground) !important; font-weight: 600 !important; }
    .encounter-registry-page .care-tab-button i { color: inherit !important; font-size: 1rem !important; }
    .encounter-registry-page .care-history-period-strip { background: transparent !important; border: 0 !important; flex-wrap: wrap !important; gap: .75rem !important; padding: 1rem 0 !important; }
    .encounter-registry-page .care-history-period-label { font-size: .75rem !important; font-weight: 500 !important; text-transform: none !important; }
    .encounter-registry-page .care-history-period-options { border-color: var(--ui-border) !important; border-radius: calc(var(--ui-radius) - 2px) !important; overflow: hidden !important; }
    .encounter-registry-page .care-history-period-button { font-size: .8125rem !important; font-weight: 500 !important; }
    .encounter-registry-page .care-filter-shell { background: transparent !important; border: 0 !important; padding: 1.25rem 0 !important; }
    .encounter-registry-page .care-search-form { background: transparent !important; border: 0 !important; box-shadow: none !important; margin: 0 !important; max-width: 34rem !important; padding: 0 !important; }
    .encounter-registry-page .care-search-form .input-field { height: auto !important; margin: 0 !important; }
    .encounter-registry-page .care-search-form input[type="search"] { background: var(--ui-background) !important; border: 1px solid var(--ui-input) !important; border-radius: calc(var(--ui-radius) - 2px) !important; box-sizing: border-box !important; font-size: .875rem !important; height: 2.75rem !important; margin: 0 !important; padding: 0 2.75rem 0 2.5rem !important; width: 100% !important; }
    .encounter-registry-page .care-search-form .label-icon { height: 2.75rem !important; left: .75rem !important; margin: 0 !important; top: 0 !important; transform: none !important; }
    .encounter-registry-page .care-search-form .label-icon i { color: var(--ui-muted-foreground) !important; font-size: 1.125rem !important; }
    .encounter-registry-page .care-search-clear { align-items: center !important; background: transparent !important; border: 0 !important; color: var(--ui-muted-foreground) !important; cursor: pointer !important; display: flex !important; height: 2.25rem !important; justify-content: center !important; padding: 0 !important; position: absolute !important; right: .25rem !important; top: .25rem !important; width: 2.25rem !important; }
    .encounter-registry-page .care-search-clear i { font-size: 1rem !important; }
    .encounter-registry-page .care-workbench .care-encounter-card.card { border: 1px solid var(--ui-border) !important; border-radius: var(--ui-radius) !important; box-shadow: 0 8px 14px -10px rgb(15 23 42 / .25) !important; margin: 0 0 1rem !important; overflow: hidden !important; }
    .encounter-registry-page .care-workbench .care-encounter-main.card,
    .encounter-registry-page .care-workbench .care-claims-card.card { border: 0 !important; box-shadow: none !important; margin: 0 !important; }
    .encounter-registry-page .care-workbench .care-table-head { background: var(--ui-muted) !important; border-bottom: 1px solid var(--ui-border) !important; color: var(--ui-muted-foreground) !important; font-size: .75rem !important; padding: .75rem 1rem !important; }
    .encounter-registry-page .care-workbench .care-encounter-row { font-size: .8125rem !important; padding: 1.25rem 1rem !important; }
    .encounter-registry-page .care-encounter-row h6 { font-size: .875rem !important; line-height: 1.6 !important; margin: 0 !important; }
    .encounter-registry-page .care-encounter-row p { font-size: .8125rem !important; line-height: 1.6 !important; margin: 0 !important; }
    .encounter-registry-page .care-workbench .care-claims-card .collection { border: 0 !important; border-top: 1px solid var(--ui-border) !important; margin: 0 !important; }
    .encounter-registry-page .care-claims-card .collection-item { font-size: .8125rem !important; line-height: 1.6 !important; padding: .85rem 2.75rem .85rem 1.25rem !important; }
    .encounter-registry-page .care-row-action { align-items: center !important; background: var(--ui-background) !important; border: 1px solid var(--ui-border) !important; border-radius: calc(var(--ui-radius) - 2px) !important; color: var(--ui-muted-foreground) !important; cursor: pointer !important; display: inline-flex !important; font-size: .8125rem !important; gap: .5rem !important; justify-content: center !important; min-height: 2.25rem !important; min-width: 2.25rem !important; padding: .4rem .6rem !important; text-decoration: none !important; }
    .encounter-registry-page .care-row-action i { color: inherit !important; font-size: 1rem !important; }
    .encounter-registry-page .care-row-action:hover { background: var(--ui-primary) !important; border-color: var(--ui-primary) !important; color: var(--ui-primary-foreground) !important; }
    .encounter-registry-page :is(button, a, input):focus-visible { outline: 2px solid var(--ui-ring) !important; outline-offset: 2px !important; }
    @media (max-width: 600px) {
        .encounter-registry-page.care-page-shell { padding: 1.25rem 1rem 2rem !important; }
        .encounter-registry-page .care-tab-strip { flex-direction: row !important; flex-wrap: wrap !important; gap: .5rem !important; }
    }
}
.progress {
    margin: 0;
    border-radius: 0;
}

.custom-hr {
    width: 50%;
    border: none;
    border-top: 2px solid #ccc;
    margin: 20px 20px;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
    color: rgb(21, 21, 206) !important;
}

.cancel-item:hover .material-icons.grey-text,
.cancel-item:hover .material-icons.grey-text::before {
    color: rgb(214, 16, 16) !important;
}

.clickable {
    cursor: pointer;
    opacity: 1;
    transition: opacity 0.3s ease;
}

.clickable:hover {
    opacity: 0.8;
}

.pointable {
    cursor: default;
    opacity: 1;
    transition: opacity 0.3s ease;
}

.pointable:hover {
    opacity: 0.8;
}

.care-page-shell {
    margin-top: -18px;
    padding-bottom: 12px;
    position: relative;
    z-index: 1;
}

:global(.sba-collection-breadcrumb) {
    background: #ffffff !important;
    background-color: #ffffff !important;
}

:global(html:has(.care-page-shell)),
:global(body:has(.care-page-shell)),
:global(.app-shell:has(.care-page-shell)),
:global(.app-shell:has(.care-page-shell) main) {
    background: var(--care-gutter-bg) !important;
    background-color: var(--care-gutter-bg) !important;
}

.care-workbench {
    color: #263238;
    transform: none !important;
}

.care-review-head {
    background: #ffffff;
    border: 1px solid #c4d3d9;
    border-radius: 6px;
    box-shadow: 0 18px 34px -26px rgba(38, 50, 56, 0.82);
    overflow: visible;
    position: relative;
    z-index: 3;
}

.care-review-head::before {
    border: 1px solid #c4d3d9;
    border-bottom: 0;
    border-radius: 7px 7px 0 0;
    content: "";
    height: 12px;
    left: -1px;
    pointer-events: none;
    position: absolute;
    right: -1px;
    top: -1px;
}

.care-title-row {
    align-items: center;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    padding: 28px 32px 24px;
}

.care-title-block h5 {
    color: #263238;
    font-size: 1.75rem;
    font-weight: 800;
    letter-spacing: 0;
    margin: 8px 0 7px;
}

.care-title-block p {
    color: #607d8b;
    font-size: 1rem;
    line-height: 1.45;
    margin: 0;
}

.care-primary-action {
    align-items: center;
    background: #00897b;
    border: 1px solid #00897b;
    border-radius: 4px;
    box-shadow: 0 10px 20px -18px rgba(38, 50, 56, 0.8);
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    font-weight: 800;
    gap: 9px;
    height: 40px;
    justify-content: center;
    letter-spacing: 0;
    min-width: 146px;
    padding: 0 16px;
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.care-primary-action:hover,
.care-primary-action:focus {
    background: #00796b;
    border-color: #00796b;
    color: #ffffff;
    box-shadow: 0 14px 24px -20px rgba(38, 50, 56, 0.85);
    outline: 0;
}

.care-primary-action i {
    color: currentColor;
    font-size: 20px;
}

.care-primary-action.is-cancel {
    background: #ffffff;
    border-color: #c62828;
    border-radius: 999px 4px 4px 999px;
    color: #c62828;
    height: 40px;
    min-width: 118px;
    padding: 0 16px 0 6px;
    position: relative;
    box-shadow: 0 10px 20px -18px rgba(117, 28, 28, 0.55);
}

.care-primary-action.is-cancel:hover,
.care-primary-action.is-cancel:focus {
    background: #fff7f7;
    border-color: #b71c1c;
    color: #b71c1c;
    box-shadow: 0 12px 22px -18px rgba(117, 28, 28, 0.65);
}

.care-primary-action.is-cancel i {
    align-items: center;
    background: #ffebee;
    border: 1px solid #c62828;
    border-radius: 999px;
    color: currentColor;
    display: inline-flex;
    height: 28px;
    justify-content: center;
    width: 28px;
}

.care-primary-action.is-cancel span {
    border-bottom: 0;
    line-height: 1.1;
    padding-bottom: 0;
}

.care-primary-action.is-cancel:hover i,
.care-primary-action.is-cancel:focus i {
    background: #ffcdd2;
    border-color: #b71c1c;
}

.care-filter-shell {
    background: #f8fafb;
    border: 1px solid #c4d3d9;
    border-top: 0;
    padding: 22px 30px;
    position: relative;
    z-index: 2;
}

.care-tab-strip {
    align-items: center;
    background: #f8fafb;
    border: 1px solid #c4d3d9;
    border-top: 0;
    display: flex;
    gap: 10px;
    padding: 16px 30px;
}

.care-tab-button {
    align-items: center;
    background: #ffffff;
    border: 1px solid #d8e4e8;
    border-radius: 6px;
    color: #455a64;
    cursor: pointer;
    display: inline-flex;
    font-weight: 800;
    gap: 8px;
    height: 40px;
    justify-content: center;
    letter-spacing: 0;
    padding: 0 16px;
}

.care-tab-button i {
    color: #00897b;
    font-size: 20px;
}

.care-tab-button.is-active {
    background: #263238;
    border-color: #263238;
    color: #ffffff;
}

.care-tab-button.is-active i {
    color: #ffffff;
}

.care-history-period-strip {
    align-items: center;
    background: #ffffff;
    border: 1px solid #c4d3d9;
    border-top: 0;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 14px 30px;
}

.care-history-period-label {
    color: #607d8b;
    font-size: 0.78rem;
    font-weight: 800;
    text-transform: uppercase;
}

.care-history-period-options {
    border: 1px solid #c9d7dc;
    display: inline-flex;
}

.care-history-period-button {
    background: #ffffff;
    border: 0;
    color: #455a64;
    cursor: pointer;
    font-weight: 800;
    height: 36px;
    letter-spacing: 0;
    padding: 0 15px;
}

.care-history-period-button + .care-history-period-button {
    border-left: 1px solid #c9d7dc;
}

.care-history-period-button.is-active {
    background: #e0f2f1;
    color: #00695c;
}

.care-search-form {
    background: #ffffff;
    border: 1px solid #d8e4e8;
    border-radius: 6px;
    margin: 0;
    padding: 0 14px;
}

.care-search-form .input-field {
    align-items: center;
    display: flex;
    height: 56px;
    margin: 0;
    position: relative;
}

.care-search-form input[type="search"] {
    border-bottom: 0 !important;
    box-shadow: none !important;
    color: #263238;
    height: 56px;
    margin: 0;
    padding-left: 46px;
    padding-right: 38px;
}

.care-search-form .label-icon {
    align-items: center;
    display: flex;
    height: 56px;
    left: 0;
    line-height: 1;
    pointer-events: none;
    position: absolute;
    top: 0;
}

.care-search-form .label-icon i {
    color: #00897b;
    line-height: 1;
}

.care-search-clear {
    color: #ef6c00;
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
}

.care-start-search-card {
    align-items: center;
    background: #ffffff;
    border: 1px solid #c9d7dc;
    border-top: 0;
    display: flex;
    gap: 14px;
    padding: 15px 30px;
}

.care-start-search-icon {
    color: #00897b;
    font-size: 21px;
    line-height: 1;
}

.care-start-search-link {
    color: #00897b;
    font-size: 14px;
    font-weight: 900;
    letter-spacing: 0;
    text-decoration: underline;
    text-underline-offset: 3px;
}

.care-start-search-link:hover,
.care-start-search-link:focus {
    color: #00695c;
    outline: 0;
}

.care-start-search-copy {
    align-items: center;
    color: #607d8b;
    display: flex;
    flex-wrap: wrap;
    font-size: 14px;
    font-weight: 700;
    gap: 6px;
}

.care-start-search-copy strong {
    color: #263238;
    font-size: 16px;
    font-weight: 900;
}

.care-encounter-card {
    background: #ffffff;
    border: 1px solid #c4d3d9;
    border-radius: 6px;
    box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
    margin: 0 0 22px;
    overflow: hidden;
}

.care-start-panel {
    background: #f4f7f8;
    border: 1px solid #c4d3d9;
    border-top: 0;
    border-radius: 0 0 6px 6px;
    box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
    padding: 28px 30px 34px;
}

.care-start-panel :deep(.section) {
    margin: 0;
    padding: 0;
}

.care-start-panel :deep(.card) {
    border-radius: 6px;
    box-shadow: none;
    margin: 0;
    width: 100%;
}

.care-start-panel :deep(.card.grey.lighten-5),
.care-start-panel :deep(.card.z-depth-0.grey.lighten-5) {
    background: #ffffff !important;
    border: 1px solid #d8e4e8;
}

.care-start-panel :deep(.card.z-depth-0.grey.lighten-4) {
    background: #f8fafb !important;
    border: 0;
    border-radius: 6px;
}

.care-start-panel :deep(nav.white) {
    border: 1px solid #d8e4e8;
    border-radius: 6px 6px 0 0;
    box-shadow: none;
}

.care-start-panel :deep(.row.card-content.blue.lighten-5) {
    background: #f8fafb !important;
    border: 1px solid #d8e4e8;
    border-top: 0;
    border-radius: 0 0 6px 6px;
    margin: 0;
}

.care-encounter-main,
.care-claims-card {
    background: transparent;
    box-shadow: none;
    margin: 0;
}

.care-table-head {
    background: #f5f8f9;
    border-bottom: 1px solid #d8e4e8;
    color: #607d8b;
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.01em;
    padding: 16px 24px 4px;
    text-transform: uppercase;
}

.care-encounter-row {
    background: #ffffff;
    border-bottom: 1px solid #e4ecef;
    padding: 20px 24px 8px;
}

.care-encounter-row:hover {
    background: #f9fcfc;
}

.care-claims-card .collection {
    border: 0;
    margin: 0;
}

.care-claims-card .collection-item {
    border-left: 0;
    border-right: 0;
}

.care-claims-card .collection-item.care-primary-care-row {
    border-right: 1px solid #d8e4e8;
}

.care-primary-care-row {
    padding-right: 128px;
    position: relative;
}

.care-primary-care-row.is-deletable {
    cursor: pointer;
}

.care-primary-care-row.is-deletable:hover {
    background: #fffafa !important;
}

.care-primary-care-chip {
    background: #ffffff;
    border: 1px solid #d8e4e8;
    border-radius: 999px;
    color: #607d8b;
    display: inline-flex;
    gap: 6px;
    align-items: center;
    font-size: 0.72rem;
    font-weight: 800;
    line-height: 1.2;
    padding: 5px 9px;
    position: absolute;
    right: 24px;
    text-transform: uppercase;
    top: 50%;
    transform: translateY(-50%);
}

.care-primary-care-chip i {
    color: #c62828;
    font-size: 1rem;
    line-height: 1;
}

.care-pending-request-row {
    cursor: pointer;
}

.care-pending-request-row:hover {
    background: #f1f4c9 !important;
}

.care-pending-request-row.is-readonly {
    cursor: default;
}

.care-pending-request-row.is-readonly:hover {
    background: #f0f4c3 !important;
}

.care-pending-request-row .secondary-content {
    pointer-events: none;
}

.care-row-action {
    box-shadow: none;
}

.care-row-lock {
    box-shadow: none;
    color: #607d8b;
    cursor: default;
}

.care-history-action-stack {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.care-history-lock-action {
    align-items: center;
    background: #ffffff;
    border: 1px solid #c9d7dc;
    color: #607d8b;
    display: inline-flex;
    height: 34px;
    justify-content: center;
    width: 42px;
}

.care-history-lock-action {
    background: #ffffff;
    cursor: default;
}

.care-history-lock-action i {
    font-size: 20px;
    line-height: 1;
}

.care-history-move-action {
    align-items: center;
    background: #e0f2f1;
    border: 1px solid #80cbc4;
    color: #00796b;
    cursor: pointer;
    display: inline-flex;
    height: 34px;
    justify-content: center;
    padding: 0;
    transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
    width: 42px;
}

.care-history-move-action:hover:not(:disabled),
.care-history-move-action:focus-visible {
    background: #b2dfdb;
    border-color: #26a69a;
    color: #004d40;
}

.care-history-move-action:focus-visible {
    outline: 2px solid #00897b;
    outline-offset: 2px;
}

.care-history-move-action:disabled {
    background: #f5f7f8;
    border-color: #d5dfe3;
    color: #90a4ae;
    cursor: not-allowed;
}

.care-history-move-action i {
    font-size: 20px;
    line-height: 1;
}

.care-encounter-date-chip {
    align-items: center;
    background: #f4f7f8;
    border: 1px solid #d8e4e8;
    color: #607d8b;
    display: inline-flex;
    font-size: 0.78rem;
    font-weight: 700;
    gap: 5px;
    line-height: 1;
    margin-top: 8px;
    min-height: 28px;
    padding: 6px 9px;
}

.care-encounter-date-chip i {
    color: #00897b;
    font-size: 15px;
    line-height: 1;
}

.care-request-timer-chip {
    align-items: center;
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: default;
    display: inline-flex;
    gap: 7px;
    font: inherit;
    font-size: 0.78rem;
    font-weight: 700;
    justify-content: flex-start;
    line-height: 1;
    min-height: 34px;
    padding: 6px 10px;
    width: 132px;
}

.care-request-timer-chip.is-pending {
    background: #fff8e1;
    border-color: #ffe082;
    color: #8d5b00;
    cursor: default;
}

.care-request-timer-chip.is-complete {
    background: #edf7ee;
    border-color: #a5d6a7;
    color: #2e7d32;
}

.care-request-timer-chip i {
    flex: 0 0 auto;
    font-size: 17px;
    line-height: 1;
}

.care-request-timer-chip span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.care-request-timer-chip.is-pending:hover,
.care-request-timer-chip.is-pending:focus {
    outline: 0;
}

.care-request-timer-chip:disabled {
    opacity: 1;
}

.care-encounter-activity {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.care-message-link {
    align-items: center;
    background: #f2f8f8;
    border: 1px solid #c9dfe0;
    border-radius: 4px;
    color: #00695c;
    cursor: pointer;
    display: inline-flex;
    gap: 6px;
    justify-content: flex-start;
    min-height: 34px;
    padding: 6px 10px;
    position: relative;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
    width: 132px;
}

.care-message-link.has-unread {
    border-color: #80cbc4;
    box-shadow: 0 0 0 2px rgba(0, 137, 123, 0.08);
}

.care-message-unread {
    align-items: center;
    background: #f9a825;
    border: 2px solid #ffffff;
    border-radius: 10px;
    color: #ffffff;
    display: inline-flex;
    font-size: 0.64rem;
    font-weight: 800;
    height: 20px;
    justify-content: center;
    line-height: 1;
    min-width: 20px;
    padding: 0 4px;
    position: absolute;
    right: -7px;
    top: -8px;
}

.care-message-link:hover,
.care-message-link:focus-visible {
    background: #e0f2f1;
    border-color: #80cbc4;
    color: #004d40;
    outline: 0;
}

.care-message-link i {
    font-size: 17px;
    line-height: 1;
}

.care-message-link span {
    font-size: 0.82rem;
    font-weight: 800;
    line-height: 1;
}

.care-message-link small {
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
}

.care-request-delete-overlay {
    align-items: center;
    background: rgba(38, 50, 56, 0.34);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    padding: 22px;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 10080;
}

.care-request-delete-modal {
    background: #ffffff;
    border: 1px solid #c9d7dc;
    border-radius: 6px;
    box-shadow: 0 18px 40px rgba(38, 50, 56, 0.18);
    max-width: 460px;
    overflow: hidden;
    width: min(100%, 460px);
}

.care-request-delete-content {
    padding: 28px 30px 22px;
}

.care-request-delete-content > span {
    color: #00897b;
    display: block;
    font-size: 0.78rem;
    font-weight: 800;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.care-request-delete-content h5 {
    color: #263238;
    font-size: 1.32rem;
    font-weight: 800;
    margin: 0 0 10px;
}

.care-request-delete-content p {
    color: #607d8b;
    line-height: 1.55;
    margin: 0;
}

.care-request-delete-content b {
    color: #263238;
}

.care-request-delete-footer {
    align-items: center;
    background: #f8fbfb;
    border-top: 1px solid #d8e4e8;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding: 16px 18px;
}

.care-request-delete-btn {
    align-items: center;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-size: 0.86rem;
    font-weight: 800;
    gap: 8px;
    min-height: 42px;
    padding: 0 16px;
    text-transform: uppercase;
}

.care-request-delete-btn.is-cancel {
    background: #ffffff;
    border: 1px solid #c9d7dc;
    color: #455a64;
}

.care-request-delete-btn.is-delete {
    background: #ef5350;
    border: 1px solid #e53935;
    color: #ffffff;
}

.care-request-delete-btn i {
    font-size: 19px;
}

.care-state-note {
    align-items: center;
    background: #f8fafb;
    border: 1px solid #d8e4e8;
    border-radius: 6px;
    color: #546e7a;
    display: flex;
    font-size: 0.95rem;
    gap: 10px;
    margin: 0 0 18px;
    padding: 13px 16px;
}

.care-state-note i {
    color: #00897b;
    font-size: 21px;
}

.care-state-note.is-error {
    background: #fffaf0;
    border-color: #f3d49b;
    color: #6d4c41;
}

.care-state-note.is-error i {
    color: #ef6c00;
}

.care-review-head,
.care-encounter-card,
.care-start-panel {
    border: 1px solid #c9d7dc;
    border-radius: 0 !important;
    box-shadow: none;
}

.care-review-head::before {
    border-color: #c9d7dc;
    border-radius: 0;
}

.care-tab-strip,
.care-history-period-strip,
.care-filter-shell {
    border-color: #c9d7dc;
    border-left-width: 1px;
    border-right-width: 1px;
}

.care-tab-strip {
    background: #ffffff;
}

.care-filter-shell {
    background: #ffffff;
}

.care-tab-button,
.care-search-form,
.care-state-note {
    background: #ffffff;
    border-color: #c9d7dc;
    border-radius: 0;
    box-shadow: none;
}

.care-encounter-card {
    margin-bottom: 18px;
    overflow: visible;
}

.care-encounter-card.card,
.care-encounter-card .card,
.care-encounter-main.card,
.care-claims-card.card,
.care-encounter-card .card-content,
.care-claims-card .collection,
.care-claims-card .collection-item {
    border-radius: 0 !important;
}

.care-table-head {
    background: #ffffff;
    border-bottom: 1px solid #c9d7dc;
}

.care-encounter-row {
    border-bottom-color: #d9e3e7;
}

.care-encounter-row:hover {
    background: #ffffff;
}

.care-claims-card .collection {
    border-left: 1px solid #d9e3e7;
    border-bottom: 1px solid #d9e3e7;
    border-top: 1px solid #d9e3e7;
}

.care-claims-card .collection-item {
    border-color: #d8e4e8;
}

.care-row-action {
    border: 1px solid #c9d7dc;
}

.care-start-panel {
    border-top: 0;
}

.care-start-panel :deep(.card),
.care-start-panel :deep(.card-panel),
.care-start-panel :deep(nav.white),
.care-start-panel :deep(.row.card-content.blue.lighten-5),
.care-start-panel :deep(.collection),
.care-start-panel :deep(.collection-item),
.care-start-panel :deep(.input-field input),
.care-start-panel :deep(.btn),
.care-start-panel :deep(.btn-large),
.care-start-panel :deep(.btn-floating) {
    box-shadow: none !important;
}

.care-start-panel :deep(.card),
.care-start-panel :deep(.card-panel),
.care-start-panel :deep(nav.white),
.care-start-panel :deep(.row.card-content.blue.lighten-5) {
    background: #ffffff !important;
    border-color: #c9d7dc !important;
    border-radius: 0 !important;
}

.care-start-panel :deep(.card.grey.lighten-5),
.care-start-panel :deep(.card.z-depth-0.grey.lighten-5),
.care-start-panel :deep(.card.z-depth-0.grey.lighten-4),
.care-start-panel :deep(.card.grey.lighten-4),
.care-start-panel :deep(.card-panel.teal.lighten-5),
.care-start-panel :deep(.card.teal.lighten-5) {
    background: #ffffff !important;
    border: 1px solid #c9d7dc !important;
}

.care-start-panel :deep(.card-content),
.care-start-panel :deep(.card-panel) {
    border-radius: 0 !important;
}

.care-start-panel :deep(.row.teal.lighten-5),
.care-start-panel :deep(.card-content.yellow.lighten-1) {
    background: #ffffff !important;
    border: 1px solid #d9e3e7;
}

.care-start-panel :deep(.row.teal.lighten-5) {
    box-sizing: border-box;
    margin: 0 30px 18px !important;
    width: auto;
}

.care-start-panel :deep(.card.grey.lighten-4 > #claim:first-child) {
    display: block;
    height: 0;
}

.care-start-panel :deep(.row.teal.lighten-5 > .col.s12) {
    box-sizing: border-box;
    padding-left: 0 !important;
    padding-right: 0 !important;
    width: 100%;
}

.care-start-panel :deep(.card-content.yellow.lighten-1) {
    box-sizing: border-box;
    width: 100%;
}

.care-start-panel :deep(.collection) {
    border: 1px solid #c9d7dc !important;
    border-radius: 0;
}

.care-start-panel :deep(.collection-item) {
    border-color: #d8e4e8 !important;
}

.care-start-panel :deep(.encounter-start-search-shell.is-not-found) {
    background: #fff7f7 !important;
}

.care-start-panel :deep(.encounter-start-search-shell.is-not-found > .card-content) {
    background: #fff7f7 !important;
}

.care-start-panel :deep(.encounter-start-search-shell.is-not-found nav.white),
.care-start-panel :deep(.encounter-start-search-shell.is-not-found .row.card-content.blue.lighten-5) {
    background: #ffffff !important;
}

.care-start-panel :deep(.input-field i.material-icons.orange) {
    align-items: center;
    background: #111111 !important;
    border-left: 1px solid #111111;
    color: #ffffff !important;
    display: inline-flex;
    justify-content: center;
}

.care-start-panel :deep(.input-field i.material-icons.orange:hover),
.care-start-panel :deep(.input-field i.material-icons.orange:focus) {
    background: #263238 !important;
    color: #ffffff !important;
}

.care-start-panel :deep(.badge.red.encounter-start-error) {
    align-items: center;
    background: #ffffff !important;
    border: 1px solid #d8e4e8;
    border-radius: 3px;
    box-shadow: none;
    color: #111111 !important;
    display: inline-flex;
    font-size: 14px;
    font-weight: 600;
    gap: 9px;
    line-height: 1.2;
    min-height: 38px;
    padding: 8px 13px;
}

.care-start-panel :deep(.badge.red.encounter-start-error i) {
    color: #c62828;
    font-size: 22px;
    line-height: 1;
}

.care-start-panel :deep(.encounter-create-workstation) {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.care-start-panel :deep(.encounter-enrollee-card),
.care-start-panel :deep(.encounter-detail-card) {
    background: #ffffff !important;
    border: 1px solid #c9d7dc !important;
    border-radius: 0 !important;
    box-shadow:
        -7px 0 15px -15px rgba(38, 50, 56, 0.45),
        7px 0 15px -15px rgba(38, 50, 56, 0.45),
        0 10px 18px -20px rgba(38, 50, 56, 0.35) !important;
    box-sizing: border-box;
    margin: 0 !important;
    width: 100%;
}

.care-start-panel :deep(.encounter-enrollee-card) {
    min-height: 260px;
    padding: 24px 30px 30px !important;
}

.care-start-panel :deep(.encounter-enrollee-card > .row) {
    align-items: center;
    display: flex;
    margin-bottom: 0;
}

.care-start-panel :deep(.encounter-scheme-badge) {
    float: none !important;
    margin: 0 0 10px !important;
}

.care-start-panel :deep(.encounter-action-card) {
    padding: 0 !important;
}

.care-start-panel :deep(.encounter-action-card-content) {
    background: transparent !important;
    border: 0 !important;
    padding: 24px 30px !important;
}

.care-start-panel :deep(.encounter-care-card) {
    padding: 0 !important;
}

.care-start-panel :deep(.encounter-care-card > .row.teal.lighten-5) {
    border-left: 0;
    border-right: 0;
    border-top: 0;
    margin: 0 !important;
}

.care-start-panel :deep(.encounter-care-card > .encounter-item-stack) {
    gap: 0;
    padding: 0;
}

.care-start-panel :deep(.encounter-visit-actions) {
    align-items: center;
    display: flex;
    gap: 12px;
    margin: 0 !important;
    min-height: 78px;
    padding: 16px 30px !important;
}

.care-start-panel :deep(.encounter-visit-spacer) {
    flex: 1 1 auto;
    float: none !important;
    margin: 0 !important;
    padding: 0 !important;
    width: auto !important;
}

.care-start-panel :deep(.encounter-visit-button) {
    align-items: center;
    border-radius: 6px;
    box-shadow: 0 12px 24px -18px rgba(38, 50, 56, 0.55) !important;
    display: inline-flex;
    float: none !important;
    font-size: 1rem;
    font-weight: 800;
    gap: 12px;
    height: 58px;
    justify-content: center;
    letter-spacing: 0;
    line-height: 1;
    margin: 0 !important;
    min-width: 176px;
    padding: 0 24px !important;
    text-transform: none !important;
    transition: background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease, transform 0.18s ease;
    width: auto !important;
}

.care-start-panel :deep(.encounter-visit-button:hover),
.care-start-panel :deep(.encounter-visit-button:focus) {
    box-shadow: 0 14px 26px -18px rgba(38, 50, 56, 0.7) !important;
    outline: 0;
    transform: translateY(-1px);
}

.care-start-panel :deep(.encounter-visit-button i.material-icons) {
    color: currentColor !important;
    font-size: 24px !important;
    line-height: 1 !important;
    margin: 0 !important;
    width: auto;
}

.care-start-panel :deep(.encounter-visit-cancel) {
    background: #ffffff !important;
    border: 1px solid #e53935 !important;
    color: #c62828 !important;
}

.care-start-panel :deep(.encounter-visit-cancel:hover),
.care-start-panel :deep(.encounter-visit-cancel:focus) {
    background: #fff7f7 !important;
    border-color: #c62828 !important;
    color: #b71c1c !important;
}

.care-start-panel :deep(.encounter-visit-save) {
    background: #00897b !important;
    border: 1px solid #00897b !important;
    color: #ffffff !important;
    min-width: 218px;
}

.care-start-panel :deep(.encounter-visit-save:hover),
.care-start-panel :deep(.encounter-visit-save:focus) {
    background: #00796b !important;
    border-color: #00796b !important;
    color: #ffffff !important;
}

.care-start-panel :deep(.encounter-visit-auth) {
    background: #263238 !important;
    border: 1px solid #263238 !important;
    color: #ffffff !important;
}

.care-start-panel :deep(.encounter-visit-auth:hover),
.care-start-panel :deep(.encounter-visit-auth:focus) {
    background: #11191c !important;
    border-color: #11191c !important;
    color: #ffffff !important;
}

.care-start-panel :deep(.encounter-item-stack) {
    --encounter-item-rail: 30px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 16px var(--encounter-item-rail) 0;
    width: 100%;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-items-card),
.care-start-panel :deep(.encounter-item-stack > .claim-collection-add-card),
.care-start-panel :deep(.encounter-item-stack > .claim-add-card),
.care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card) {
    box-sizing: border-box;
    margin: 0 !important;
    width: 100% !important;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-items-card > .card-content),
.care-start-panel :deep(.encounter-item-stack > .claim-collection-add-card > .card-content),
.care-start-panel :deep(.encounter-item-stack > .claim-add-card > .card-content) {
    box-sizing: border-box;
    width: 100%;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-items-card > .card-content) {
    padding: 24px 30px 8px !important;
}

.care-start-panel :deep(.encounter-item-stack > .claim-collection-add-card > .card-content) {
    padding: 32px 54px 28px !important;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card > .card-content) {
    padding: 32px 54px 28px !important;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card) {
    margin-top: 0 !important;
    min-height: 0;
}

.care-start-panel :deep(.encounter-item-stack > .claim-collection-add-card),
.care-start-panel :deep(.encounter-item-stack > .claim-add-card) {
    border: 0 !important;
    border-top: 1px solid #d8e4e8 !important;
    box-shadow: none !important;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card) {
    border-top: 1px solid #e1ebef !important;
    padding-top: 18px;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card > .card-content > .row),
.care-start-panel :deep(.encounter-item-stack > .claim-add-card form),
.care-start-panel :deep(.encounter-item-stack > .claim-add-card form > .row) {
    margin-bottom: 0 !important;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card .card-title) {
    align-items: center;
    display: flex;
    justify-content: space-between;
    line-height: 1.2;
    margin: 0 0 14px;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card .input-field) {
    margin-top: 10px;
    margin-bottom: 10px;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card .input-field input) {
    height: 2.8rem;
    margin-bottom: 6px;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card .claim-entry-actions) {
    gap: 32px;
    justify-content: space-between;
    margin: 16px 0 0;
    min-height: 66px;
    padding: 12px 24px !important;
}

.care-start-panel :deep(.encounter-item-stack > .claim-add-card .claim-entry-cancel) {
    margin-right: 0;
}

.care-start-panel :deep(.encounter-item-stack .encounter-items-card .card-title) {
    display: block;
    margin: 0 0 14px;
    padding-left: 60px;
    padding-right: 28px;
}

.care-start-panel :deep(.encounter-selected-items-box) {
    border: 1px solid #c9d7dc;
    box-sizing: border-box;
    margin: 0 -12px;
    width: calc(100% + 24px);
}

.care-start-panel :deep(.encounter-selected-items-box .collection) {
    border: 0 !important;
    margin: 0 !important;
}

.care-start-panel :deep(.encounter-selected-items-box .collection + .collection) {
    border-top: 1px solid #d8e4e8 !important;
}

.care-start-panel :deep(.encounter-selected-total-list .collection-item) {
    padding-left: 72px !important;
}

.care-start-panel :deep(.encounter-submit-actions) {
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    margin: 28px -12px 0;
    width: calc(100% + 24px);
}

.care-start-panel :deep(.encounter-secondary-actions) {
    align-items: center;
    display: flex;
    gap: 12px;
}

.care-start-panel :deep(.encounter-action-button) {
    align-items: center;
    border: 1px solid #c9d7dc;
    border-radius: 4px;
    box-shadow: none;
    cursor: pointer;
    display: inline-flex;
    font-size: 0.9rem;
    font-weight: 800;
    gap: 10px;
    height: 46px;
    justify-content: center;
    letter-spacing: 0;
    line-height: 1;
    min-width: 134px;
    padding: 0 18px;
    text-transform: uppercase;
}

.care-start-panel :deep(.encounter-action-button i) {
    font-size: 22px;
    line-height: 22px;
}

.care-start-panel :deep(.encounter-action-add) {
    background: #263238;
    border-color: #263238;
    color: #ffffff;
}

.care-start-panel :deep(.encounter-action-clear) {
    background: #ffffff;
    color: #455a64;
}

.care-start-panel :deep(.encounter-action-clear i) {
    color: #00897b;
}

.care-start-panel :deep(.encounter-action-submit) {
    background: #43a047;
    border-color: #43a047;
    color: #ffffff;
    min-width: 178px;
}

.care-start-panel :deep(.encounter-action-button.disabled) {
    opacity: 0.45;
    pointer-events: none;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-items-card ol.collection),
.care-start-panel :deep(.encounter-item-stack > .encounter-items-card ul.collection),
.care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card ol.collection) {
    box-sizing: border-box;
    counter-reset: encounter-list-row;
    list-style: none;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0 !important;
    width: 100% !important;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-items-card ol.collection),
.care-start-panel :deep(.encounter-item-stack > .encounter-items-card ul.collection) {
    margin-top: 0 !important;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card ol.collection) {
    border: 0 !important;
    margin-left: -15px !important;
    margin-right: -15px !important;
    width: calc(100% + 30px) !important;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-items-card ol.collection .collection-item),
.care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card ol.collection .collection-item) {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    gap: 12px;
    min-height: 48px;
    padding-left: 24px !important;
    padding-right: 58px !important;
    position: relative;
    width: 100%;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card ol.collection .care-primary-care-row) {
    padding-right: 138px !important;
}

.care-start-panel :deep(.encounter-item-stack > .encounter-items-card ol.collection .collection-item::before),
.care-start-panel :deep(.encounter-item-stack > .encounter-approvals-card ol.collection .collection-item::before) {
    color: #263238;
    content: counter(encounter-list-row) ".";
    counter-increment: encounter-list-row;
    flex: 0 0 36px;
    font-weight: 400;
    text-align: right;
}

.care-start-panel :deep(.circle.responsive-img) {
    border: 1px solid #c9d7dc;
    box-shadow: none;
}

.care-workbench .care-encounter-card,
.care-workbench .care-encounter-card.card,
.care-workbench .care-encounter-card .card,
.care-workbench .care-encounter-main,
.care-workbench .care-claims-card,
.care-workbench .care-claims-card .collection,
.care-workbench .care-claims-card .collection-item,
.care-workbench .care-table-head,
.care-workbench .care-encounter-row {
    border-radius: 0 !important;
    box-shadow: none !important;
}

.care-workbench .care-encounter-card {
    border: 1px solid #c9d7dc !important;
    margin-bottom: 22px;
}

.care-workbench .care-encounter-main {
    border: 0 !important;
}

.care-workbench .care-row-action {
    box-shadow: none !important;
}

.care-workbench > .care-review-head,
.care-workbench > .care-start-panel,
.care-workbench .care-encounter-card.card {
    box-shadow:
        -8px 0 16px -15px rgba(38, 50, 56, 0.45),
        8px 0 16px -15px rgba(38, 50, 56, 0.45),
        0 10px 18px -20px rgba(38, 50, 56, 0.35) !important;
}

.care-workbench > .care-review-head {
    border-radius: 6px !important;
}

.care-workbench > .care-review-head::before {
    border-radius: 7px 7px 0 0;
}

@media only screen and (max-width: 600px) {
    .care-title-row,
    .care-filter-shell,
    .care-start-search-card,
    .care-history-period-strip,
    .care-tab-strip {
        align-items: flex-start;
        flex-direction: column;
        padding-left: 18px;
        padding-right: 18px;
    }

    .care-primary-action,
    .care-tab-button {
        width: 100%;
    }

    .care-history-period-options {
        display: flex;
        width: 100%;
    }

    .care-history-period-button {
        flex: 1 1 0;
        min-width: 0;
        padding: 0 8px;
    }

    .care-start-panel :deep(.encounter-submit-actions),
    .care-start-panel :deep(.encounter-secondary-actions) {
        align-items: stretch;
        flex-direction: column;
    }

    .care-start-panel :deep(.encounter-action-button) {
        width: 100%;
    }

    .care-start-panel :deep(.encounter-visit-actions) {
        align-items: stretch;
        flex-direction: column;
        padding-left: 18px !important;
        padding-right: 18px !important;
    }

    .care-start-panel :deep(.encounter-visit-button) {
        width: 100% !important;
    }

    .care-start-panel :deep(.encounter-visit-spacer) {
        display: none;
    }

    .care-start-panel :deep(.encounter-enrollee-card > .row) {
        display: block;
    }
}
</style>
